<template>
  <button
    type="button"
    class="flex h-10 flex-row items-center justify-center gap-3 rounded-full bg-primary-100 px-3 dark:bg-primary-700"
    @click="open"
  >
    <FontAwesomeIcon :icon="['fal', 'bolt']" class="dark:text-gray-300" />
    <span
      class="hidden text-gray-500 capitalize-first sm:inline dark:text-white"
    >
      {{ $t('global.search') }}
    </span>
    <span
      class="hidden items-center gap-0.5 text-gray-500 sm:flex dark:text-gray-400"
    >
      <kbd>{{ isMac ? '⌘' : 'Ctrl' }}</kbd>
      +
      <kbd>K</kbd>
    </span>
  </button>

  <TransitionRoot :show="isOpen" as="template" appear>
    <Dialog as="div" class="relative z-500" @close="close">
      <TransitionChild
        as="template"
        enter="ease-out duration-300"
        enter-from="opacity-0"
        enter-to="opacity-100"
        leave="ease-in duration-200"
        leave-from="opacity-100"
        leave-to="opacity-0"
      >
        <div
          class="fixed inset-0 bg-primary-100/75 transition-opacity dark:bg-black/75"
        />
      </TransitionChild>

      <div class="fixed inset-0 z-500 overflow-y-auto p-4 sm:p-6 md:p-20">
        <TransitionChild
          as="template"
          enter="ease-out duration-300"
          enter-from="opacity-0 scale-95"
          enter-to="opacity-100 scale-100"
          leave="ease-in duration-200"
          leave-from="opacity-100 scale-100"
          leave-to="opacity-0 scale-95"
        >
          <DialogPanel
            class="mx-auto max-w-2xl transform divide-y divide-gray-100 overflow-hidden rounded-xl bg-white shadow-2xl ring-1 ring-black/5 transition-all dark:divide-slate-700 dark:bg-slate-800 dark:text-white"
          >
            <Combobox v-model="selectedResult">
              <div class="flex flex-row items-center gap-2 px-4">
                <FontAwesomeIcon
                  :icon="['fal', loading ? 'spinner-third' : 'bolt']"
                  :spin="loading"
                  class="pointer-events-none h-5 w-5 shrink-0 text-slate-400"
                  aria-hidden="true"
                />
                <ComboboxInput
                  ref="searchInput"
                  class="h-12 flex-1 border-0 bg-transparent text-gray-800 placeholder-slate-300 focus:border-0 focus:outline-hidden sm:text-sm dark:text-white"
                  :placeholder="$t('global.search')"
                  autocomplete="off"
                  :display-value="() => searchValue"
                  @change="searchValue = $event.target.value"
                />
              </div>

              <ComboboxOptions
                v-if="groups.length"
                static
                hold
                class="max-h-96 scroll-py-2 overflow-y-auto px-4 py-2 text-sm text-gray-700 dark:text-white"
              >
                <li v-for="group in groups" :key="group.key" class="py-1">
                  <h2
                    class="flex items-center gap-2 px-2 py-1 font-bold capitalize-first"
                  >
                    <FontAwesomeIcon
                      :icon="['fal', group.icon]"
                      fixed-width
                      class="text-primary-500"
                    />
                    {{ $t(group.label) }}
                  </h2>
                  <ul>
                    <ComboboxOption
                      v-for="option in group.results"
                      :key="`${group.key}-${option.id}`"
                      v-slot="{ active }"
                      :value="option"
                      as="template"
                    >
                      <li
                        class="flex cursor-pointer flex-row items-center gap-x-2 rounded-md px-2 py-2 select-none"
                        :class="
                          active
                            ? 'bg-primary-50 text-gray-900 dark:bg-slate-700 dark:text-white'
                            : ''
                        "
                      >
                        <span class="truncate capitalize-first">
                          {{ option.title }}
                        </span>
                        <small
                          v-if="option.subtitle"
                          class="shrink-0 text-slate-500 dark:text-slate-300"
                        >
                          {{ option.subtitle }}
                        </small>
                      </li>
                    </ComboboxOption>
                  </ul>
                </li>
              </ComboboxOptions>

              <p
                v-else-if="message"
                class="px-6 py-10 text-center text-sm text-gray-500 capitalize-first dark:text-white"
              >
                {{ message }}
              </p>
            </Combobox>
          </DialogPanel>
        </TransitionChild>
      </div>
    </Dialog>
  </TransitionRoot>
</template>

<script>
import {
  Combobox,
  ComboboxInput,
  ComboboxOption,
  ComboboxOptions,
  Dialog,
  DialogPanel,
  TransitionChild,
  TransitionRoot
} from '@headlessui/vue'
import { FontAwesomeIcon } from '@fortawesome/vue-fontawesome'
import { mapActions } from 'vuex'

import apiClient from '@u/apiClient'
import { debounce } from '@u/debounce'

/**
 * Model groups of `api/search-global`, in display order: label, icon, how
 * to title a result, and where it leads (with the store action the former
 * dropdown dispatched before navigating).
 */
const GROUPS = [
  {
    key: 'App\\Models\\Work',
    label: 'global.work',
    icon: 'folder-open',
    title: (obj) => obj.searchable?.title,
    subtitle: (obj) => obj.searchable?.reference,
    route: 'work_form_edit_route'
  },
  {
    key: 'App\\Models\\Company',
    label: 'global.company',
    icon: 'landmark',
    title: (obj) => obj.searchable?.name,
    route: 'settings_edit_company_route',
    fetch: (vm, id) => vm.fetchCompanyByID(id)
  },
  {
    key: 'App\\Models\\Implantation',
    label: 'global.implantation',
    icon: 'hotel',
    title: (obj) => obj.searchable?.name,
    route: 'settings_edit_implantation_route',
    fetch: (vm, id) => {
      vm.fetchImplantationByID({ id })
      vm.fetchNewAsset()
    }
  },
  {
    key: 'App\\Models\\Asset',
    label: 'global.asset',
    icon: 'landmark',
    title: (obj) => obj.searchable?.name,
    route: 'settings_edit_asset_route',
    fetch: (vm, id) => vm.fetchAssetByID(id)
  },
  {
    key: 'App\\Models\\Item',
    label: 'global.item',
    icon: 'ticket',
    title: (obj) => obj.title ?? obj.searchable?.name,
    route: 'settings_edit_item_route',
    fetch: (vm, id) => vm.fetchItemByID({ id })
  },
  {
    key: 'App\\Models\\User',
    label: 'global.user',
    icon: 'user',
    title: (obj) => obj.title,
    route: 'settings_edit_user_route',
    fetch: (vm, id) => vm.fetchUserByID({ id })
  }
]

const MIN_CHARS = 3

/**
 * Global search as Funeral Manager's InstantSearchPalette: a button in the
 * top bar (and Cmd/Ctrl+K) opens a command palette fed by Crema's
 * `api/search-global`; arrows + Enter or a click open the result.
 */
export default {
  name: 'SearchAllComponent',
  components: {
    Combobox,
    ComboboxInput,
    ComboboxOption,
    ComboboxOptions,
    Dialog,
    DialogPanel,
    FontAwesomeIcon,
    TransitionChild,
    TransitionRoot
  },
  data() {
    return {
      isOpen: false,
      searchValue: '',
      searchResult: {},
      loading: false,
      searchFailed: false,
      selectedResult: null,
      requestId: 0
    }
  },
  computed: {
    isMac() {
      return /mac/i.test(
        navigator.userAgentData?.platform ?? navigator.platform
      )
    },
    groups() {
      return GROUPS.map((group) => ({
        ...group,
        results: (this.searchResult?.[group.key] ?? []).map((obj) => ({
          id: obj.searchable?.id,
          title: group.title(obj),
          subtitle: group.subtitle?.(obj),
          group
        }))
      })).filter((group) => group.results.length)
    },
    message() {
      if (this.searchFailed) return this.$t('global.search_failed')
      if (this.loading) return null
      if (this.searchValue.length > 0 && this.searchValue.length < MIN_CHARS) {
        return this.$t('global.search_min_chars')
      }
      if (this.searchValue.length >= MIN_CHARS) {
        return this.$t('global.no_result_found')
      }
      return null
    }
  },
  watch: {
    searchValue(value) {
      this.debouncedSearch(value)
    },
    selectedResult(option) {
      if (option) this.openResult(option)
    }
  },
  created() {
    this.debouncedSearch = debounce((value) => this.search(value), 200)
  },
  mounted() {
    window.addEventListener('keydown', this.onKeydown)
  },
  beforeUnmount() {
    window.removeEventListener('keydown', this.onKeydown)
  },
  methods: {
    ...mapActions({
      fetchCompanyByID: 'company/fetchCompanyByID',
      fetchImplantationByID: 'implantation/fetchImplantationByID',
      fetchAssetByID: 'asset/fetchAssetByID',
      fetchNewAsset: 'asset/fetchNewAsset',
      fetchItemByID: 'item/fetchItemByID',
      fetchUserByID: 'user/fetchUserByID'
    }),
    open() {
      this.isOpen = true
      this.$nextTick(() => this.$refs.searchInput?.$el?.select())
    },
    close() {
      this.isOpen = false
      this.selectedResult = null
    },
    async search(value) {
      // Only the latest request may update the results.
      const requestId = ++this.requestId
      if (!value || value.length < MIN_CHARS) {
        this.searchResult = {}
        this.searchFailed = false
        this.loading = false
        return
      }
      this.loading = true
      this.searchFailed = false
      try {
        const response = await apiClient.get('api/search-global', {
          params: { term: value }
        })
        if (requestId === this.requestId) this.searchResult = response.data
      } catch {
        if (requestId === this.requestId) this.searchFailed = true
      } finally {
        if (requestId === this.requestId) this.loading = false
      }
    },
    openResult(option) {
      option.group.fetch?.(this, option.id)
      this.$router.push({ name: option.group.route, params: { id: option.id } })
      this.close()
    },
    onKeydown(event) {
      const modifier = this.isMac ? event.metaKey : event.ctrlKey
      if (modifier && event.key.toLowerCase() === 'k') {
        event.preventDefault()
        this.open()
      }
    }
  }
}
</script>
